import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/utils/cn'
import { useUIStore } from '@/stores/uiStore'
import { useSearchStore } from '@/stores/searchStore'
import { useTranslation } from '@/stores/i18nStore'
import { useAdminFiles, useLoadFile } from '@/hooks/useSearch'
import { useAnalytics } from '@/hooks/useAnalytics'
import {
  SearchIcon,
  FolderIcon,
  HistoryIcon,
  StarIcon,
  SettingsIcon,
  CheckIcon,
  XIcon,
  EditIcon,
  GlobeIcon,
  PenToolIcon,
  BookIcon
} from '@/components/ui/icons'
import type { SidebarTab, AdminFile } from '@/types'

export function Sidebar() {
  const { t } = useTranslation()
  const { trackPageChange, trackTabChange, trackSidebarToggle, trackFileSelect } = useAnalytics()
  const {
    sidebarCollapsed,
    activeTab,
    setActiveTab,
    isMobile,
    setSidebarCollapsed,
    setCurrentPage,
    currentPage
  } = useUIStore()

  const { currentFileInfo } = useSearchStore()
  const { data: adminFiles, isLoading: filesLoading } = useAdminFiles()
  const loadFile = useLoadFile()

  const menuItems = [
    { id: 'search' as SidebarTab, icon: SearchIcon, label: t('search') },
    { id: 'editor' as SidebarTab, icon: PenToolIcon, label: t('editor') },
    { id: 'dictionary' as SidebarTab, icon: BookIcon, label: t('dictionary') },
    { id: 'files' as SidebarTab, icon: FolderIcon, label: t('files') },
    { id: 'history' as SidebarTab, icon: HistoryIcon, label: t('history') },
    { id: 'favorites' as SidebarTab, icon: StarIcon, label: t('favorites') },
    { id: 'settings' as SidebarTab, icon: SettingsIcon, label: t('settings') }
  ]

  const handleMenuItemClick = (itemId: SidebarTab) => {
    const previousPage = currentPage
    const previousTab = activeTab

    if (itemId === 'editor') {
      trackPageChange('editor', previousPage)
      setCurrentPage('editor')
    } else if (itemId === 'dictionary') {
      trackPageChange('dictionary', previousPage)
      setCurrentPage('dictionary')
    } else {
      if (currentPage !== 'main') {
        trackPageChange('main', previousPage)
      }
      trackTabChange(itemId, previousTab)
      setCurrentPage('main')
      setActiveTab(itemId)
    }
    if (isMobile) {
      trackSidebarToggle(false)
      setSidebarCollapsed(true)
    }
  }

  const handleFileClick = async (file: AdminFile) => {
    trackFileSelect(parseInt(file.id), file.filename)
    loadFile.mutate(file.id)
    if (isMobile) {
      trackSidebarToggle(false)
      setSidebarCollapsed(true)
    }
  }

  const sidebarWidth = sidebarCollapsed && !isMobile ? '64px' : '280px'
  const isHidden = isMobile && sidebarCollapsed

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMobile && !sidebarCollapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-40 md:hidden"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          x: isHidden ? -280 : 0,
          width: sidebarWidth
        }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className={cn(
          'fixed top-16 left-0 h-[calc(100vh-64px)] z-40',
          'bg-white dark:bg-zinc-950',
          'border-r border-zinc-200 dark:border-zinc-800',
          'flex flex-col overflow-hidden'
        )}
      >
        {/* Close button on mobile */}
        {isMobile && !sidebarCollapsed && (
          <button
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 z-10"
            onClick={() => setSidebarCollapsed(true)}
          >
            <XIcon className="w-5 h-5 text-zinc-400" />
          </button>
        )}

        {/* Navigation Menu */}
        <nav className="py-3 px-3">
          {menuItems.map((item) => {
            const Icon = item.icon
            const isActive = item.id === 'editor'
              ? currentPage === 'editor'
              : item.id === 'dictionary'
              ? currentPage === 'dictionary'
              : currentPage === 'main' && activeTab === item.id
            const fileCount = item.id === 'files' ? adminFiles?.length : null

            return (
              <button
                key={item.id}
                onClick={() => handleMenuItemClick(item.id)}
                className={cn(
                  'w-full flex items-center gap-3 my-0.5 rounded-lg transition-colors',
                  sidebarCollapsed && !isMobile
                    ? 'justify-center p-3'
                    : 'px-3 py-2.5',
                  isActive
                    ? 'bg-zinc-100 dark:bg-zinc-900 text-zinc-900 dark:text-white'
                    : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-900'
                )}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!(sidebarCollapsed && !isMobile) && (
                  <>
                    <span className="text-sm font-medium">{item.label}</span>
                    {fileCount != null && fileCount > 0 && (
                      <span className="ml-auto text-xs text-zinc-400 dark:text-zinc-500">
                        {fileCount}
                      </span>
                    )}
                  </>
                )}
              </button>
            )
          })}
        </nav>

        {/* Divider */}
        {!(sidebarCollapsed && !isMobile) && (
          <div className="mx-4 h-px bg-zinc-200 dark:bg-zinc-800" />
        )}

        {/* Content Area */}
        {!(sidebarCollapsed && !isMobile) && (
          <div className="flex-1 px-4 py-4 overflow-y-auto">
            <AnimatePresence mode="wait">
              {activeTab === 'search' && (
                <SidebarContent key="search">
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
                    {t('selectFileToStart')}
                  </p>

                  {currentFileInfo && (
                    <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-900">
                      <div className="flex items-center gap-3">
                        <CheckIcon className="w-5 h-5 text-emerald-500" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-emerald-900 dark:text-emerald-100 text-sm truncate">
                            {currentFileInfo.filename}
                          </p>
                          <p className="text-xs text-emerald-600 dark:text-emerald-400">
                            {t('readyToSearch')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </SidebarContent>
              )}

              {activeTab === 'files' && (
                <SidebarContent key="files">
                  <div className="space-y-2">
                    {filesLoading ? (
                      <>
                        <FileSkeleton />
                        <FileSkeleton />
                      </>
                    ) : adminFiles?.length === 0 ? (
                      <EmptyState
                        icon={FolderIcon}
                        title={t('noFiles')}
                        description={t('uploadFilesToSearch')}
                      />
                    ) : (
                      <BookShelf
                        files={adminFiles || []}
                        selectedFileId={currentFileInfo?.fileId}
                        onFileClick={handleFileClick}
                      />
                    )}
                  </div>
                </SidebarContent>
              )}

              {activeTab === 'history' && (
                <SidebarContent key="history">
                  <EmptyState
                    icon={HistoryIcon}
                    title={t('noHistory')}
                    description={t('recentSearchesAppearHere')}
                  />
                </SidebarContent>
              )}

              {activeTab === 'favorites' && (
                <SidebarContent key="favorites">
                  <EmptyState
                    icon={StarIcon}
                    title={t('noFavorites')}
                    description={t('starSearchesToSaveThem')}
                  />
                </SidebarContent>
              )}

              {activeTab === 'settings' && (
                <SidebarContent key="settings">
                  <SettingsPanel />
                </SidebarContent>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Footer */}
        {!(sidebarCollapsed && !isMobile) && (
          <div className="p-4 border-t border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-zinc-900 dark:bg-white flex items-center justify-center">
                <span className="text-white dark:text-zinc-900 font-bold text-xs font-ethiopic">ሣ</span>
              </div>
              <span className="text-xs text-zinc-400">SatMas v2.0</span>
            </div>
          </div>
        )}
      </motion.aside>
    </>
  )
}

function SettingsPanel() {
  const { t, locale, setLocale } = useTranslation()
  const { setCurrentPage } = useUIStore()
  const { trackLanguageChange, trackPageChange } = useAnalytics()

  const handleLanguageChange = (newLocale: 'en' | 'am') => {
    trackLanguageChange(newLocale, locale)
    setLocale(newLocale)
  }

  const handleOpenTranslationEditor = () => {
    trackPageChange('translations', 'main')
    setCurrentPage('translations')
  }

  return (
    <div className="space-y-3">
      {/* Language Selector */}
      <div className="p-3 rounded-lg bg-zinc-50 dark:bg-zinc-900">
        <div className="flex items-center gap-2 mb-2">
          <GlobeIcon className="w-4 h-4 text-zinc-500" />
          <p className="font-medium text-sm text-zinc-700 dark:text-zinc-300">{t('language')}</p>
        </div>
        <p className="text-xs text-zinc-400 mb-3">{t('languageDescription')}</p>
        <div className="flex gap-2">
          <button
            onClick={() => handleLanguageChange('en')}
            className={cn(
              'flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors',
              locale === 'en'
                ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900'
                : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-300 dark:hover:bg-zinc-700'
            )}
          >
            English
          </button>
          <button
            onClick={() => handleLanguageChange('am')}
            className={cn(
              'flex-1 px-3 py-2 rounded-md text-sm font-medium font-ethiopic transition-colors',
              locale === 'am'
                ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900'
                : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-300 dark:hover:bg-zinc-700'
            )}
          >
            አማርኛ
          </button>
        </div>
      </div>

      {/* Theme */}
      <div className="p-3 rounded-lg bg-zinc-50 dark:bg-zinc-900">
        <p className="font-medium text-sm text-zinc-700 dark:text-zinc-300">{t('theme')}</p>
        <p className="text-xs text-zinc-400 mt-0.5">{t('themeDescription')}</p>
      </div>

      {/* Edit Translations */}
      <div className="p-3 rounded-lg bg-zinc-50 dark:bg-zinc-900">
        <div className="flex items-center gap-2 mb-1">
          <EditIcon className="w-4 h-4 text-zinc-500" />
          <p className="font-medium text-sm text-zinc-700 dark:text-zinc-300">{t('editTranslations')}</p>
        </div>
        <p className="text-xs text-zinc-400 mb-3">{t('editTranslationsDescription')}</p>
        <button
          onClick={handleOpenTranslationEditor}
          className="w-full px-3 py-2 rounded-md text-sm font-medium bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-300 dark:hover:bg-zinc-700 transition-colors"
        >
          {t('translationEditor')}
        </button>
      </div>
    </div>
  )
}

function SidebarContent({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      {children}
    </motion.div>
  )
}

function FileSkeleton() {
  return (
    <div className="flex items-center gap-3 p-3">
      <div className="w-9 h-9 rounded-lg bg-zinc-200 dark:bg-zinc-800 animate-pulse" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-24 bg-zinc-200 dark:bg-zinc-800 rounded animate-pulse" />
        <div className="h-3 w-16 bg-zinc-200 dark:bg-zinc-800 rounded animate-pulse" />
      </div>
    </div>
  )
}

interface EmptyStateProps {
  icon: typeof SearchIcon
  title: string
  description: string
}

function EmptyState({ icon: Icon, title, description }: EmptyStateProps) {
  return (
    <div className="text-center py-8">
      <div className="w-12 h-12 mx-auto mb-3 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
        <Icon className="w-6 h-6 text-zinc-400" />
      </div>
      <p className="font-medium text-zinc-600 dark:text-zinc-400 text-sm mb-1">{title}</p>
      <p className="text-xs text-zinc-400">{description}</p>
    </div>
  )
}

// Book colors for Amazon-style covers
const BOOK_COLORS = [
  { bg: 'from-amber-600 to-amber-800' },
  { bg: 'from-emerald-600 to-emerald-800' },
  { bg: 'from-sky-600 to-sky-800' },
  { bg: 'from-rose-600 to-rose-800' },
  { bg: 'from-violet-600 to-violet-800' },
  { bg: 'from-orange-600 to-orange-800' },
  { bg: 'from-teal-600 to-teal-800' },
  { bg: 'from-indigo-600 to-indigo-800' },
]

interface BookShelfProps {
  files: AdminFile[]
  selectedFileId?: string
  onFileClick: (file: AdminFile) => void
}

function BookShelf({ files, selectedFileId, onFileClick }: BookShelfProps) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {files.map((file, index) => {
        const color = BOOK_COLORS[index % BOOK_COLORS.length]
        const isSelected = selectedFileId === file.id
        const hasCover = file.coverUrl

        return (
          <motion.button
            key={file.id}
            onClick={() => onFileClick(file)}
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.98 }}
            className={cn(
              'flex flex-col items-center gap-1.5 p-2 rounded-lg transition-colors',
              isSelected
                ? 'bg-zinc-100 dark:bg-zinc-800'
                : 'hover:bg-zinc-50 dark:hover:bg-zinc-900'
            )}
          >
            {/* Book Cover */}
            <div
              className={cn(
                'relative w-16 h-20 rounded shadow-md overflow-hidden',
                !hasCover && 'bg-gradient-to-br',
                !hasCover && color.bg,
                isSelected && 'ring-2 ring-emerald-500 ring-offset-2 ring-offset-white dark:ring-offset-zinc-950'
              )}
            >
              {hasCover ? (
                /* Actual cover image */
                <img
                  src={file.coverUrl}
                  alt={file.filename}
                  className="w-full h-full object-cover"
                />
              ) : (
                /* Placeholder cover design */
                <>
                  {/* Spine shadow */}
                  <div className="absolute inset-y-0 left-0 w-1 bg-black/20" />

                  {/* Simple decorative design */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-1.5">
                    <div className="w-8 h-px bg-white/40" />
                    <div className="w-5 h-px bg-white/30 mt-1.5" />
                    <div className="w-6 h-px bg-white/30 mt-1" />
                  </div>

                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent" />
                </>
              )}

              {/* Page edges */}
              <div className="absolute top-0.5 bottom-0.5 right-0 w-0.5 bg-zinc-200" />

              {/* Selected checkmark */}
              {isSelected && (
                <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
                  <CheckIcon className="w-2.5 h-2.5 text-white" />
                </div>
              )}
            </div>

            {/* Title below */}
            <p className={cn(
              'text-[10px] text-center line-clamp-2 w-full leading-tight font-ethiopic',
              isSelected
                ? 'text-zinc-900 dark:text-white font-medium'
                : 'text-zinc-500 dark:text-zinc-400'
            )}>
              {file.filename.replace(/\.[^/.]+$/, '')}
            </p>
          </motion.button>
        )
      })}
    </div>
  )
}
